import { useMemo, useState } from 'react';
import { getAllOrgUnitsFromGoogleQuery } from '../../../../api/users-api';
import { useQuery } from '@tanstack/react-query';
import { DecomModel } from '../../../../../types';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
interface DecomOrgAutoCompleteProps {
  autoCompleteValue: string;
  setAutoCompleteValue: (value: string) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  hasError?: boolean;
  newKey?: string;
  helperText?: string;
  decomModels: DecomModel[];
}

export default function DecomOrgAutoComplete({
  autoCompleteValue,
  setAutoCompleteValue,
  size,
  disabled,
  hasError,
  newKey,
  helperText,
  decomModels,
}: DecomOrgAutoCompleteProps) {
  const [inputValue, setInputValue] = useState('');

  const key = useMemo(
    () => newKey || new Date().getTime().toString(),
    [newKey]
  );

  const { data: orgUnits, isLoading } = useQuery(
    getAllOrgUnitsFromGoogleQuery()
  );

  const allCurrentlyUsedOrgs = decomModels.flatMap((model) => model.org);

  return (
    <Autocomplete
      key={key}
      id="org-units-autocomplete"
      disabled={disabled || isLoading}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 400,
          },
        },
      }}
      value={
        orgUnits?.find((ou) => ou.orgUnitPath === autoCompleteValue) || null
      }
      onChange={(_event, newValue) => {
        setAutoCompleteValue(newValue?.orgUnitPath || '');
      }}
      getOptionLabel={(option) => option.orgUnitPath}
      options={
        orgUnits?.sort((a, b) => a.orgUnitPath.localeCompare(b.orgUnitPath)) ||
        []
      }
      size={size}
      autoComplete
      autoHighlight
      noOptionsText="No OU's Found"
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search for an organizational unit"
          required
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          fullWidth
          error={hasError}
          helperText={helperText}
        />
      )}
      getOptionDisabled={(option) =>
        allCurrentlyUsedOrgs.includes(option.orgUnitPath)
      }
      renderOption={(props, option) => (
        <Tooltip
          key={option.orgUnitId}
          title={
            allCurrentlyUsedOrgs.includes(option.orgUnitPath)
              ? 'Org Units can only be assigned to one decommissioning policy.'
              : ''
          }
          placement="right"
          arrow
        >
          <span>
            <ListItem {...props} key={option.orgUnitId}>
              {option.orgUnitPath}
            </ListItem>
          </span>
        </Tooltip>
      )}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
    />
  );
}
